import React from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
} from '@mui/material'
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

export interface BreadcrumbItem {
  label: string
  path?: string
  icon?: React.ReactNode
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[]
}

// Route label mapping for automatic breadcrumb generation
const routeLabelMap: Record<string, string> = {
  dashboard: 'Dashboard',
  equipment: 'Equipment',
  inspections: 'Inspections',
  checklists: 'Checklists',
  maintenance: 'Maintenance',
  inventory: 'Inventory',
  reports: 'Reports',
  users: 'Users',
  tenants: 'Tenants',
  departments: 'Departments',
  'my-permissions': 'My Permissions',
  settings: 'Settings',
  admin: 'Administration',
  examples: 'Examples',
  permissions: 'Permissions',
  shadcn: 'Shadcn Demo',
  'permission-requests': 'Permission Requests',
}

/**
 * Generates breadcrumb items from the current route path
 */
const generateBreadcrumbsFromPath = (pathname: string): BreadcrumbItem[] => {
  // Remove leading slash and split by '/'
  const segments = pathname.split('/').filter(Boolean)
  
  if (segments.length === 0) {
    return [{ label: 'Dashboard', path: '/dashboard' }]
  }

  const breadcrumbs: BreadcrumbItem[] = []
  let currentPath = ''

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`
    const label = routeLabelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1)
    
    // Don't add path for the last item (current page)
    if (index === segments.length - 1) {
      breadcrumbs.push({ label })
    } else {
      breadcrumbs.push({ label, path: currentPath })
    }
  })

  return breadcrumbs
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const { t } = useTranslation()
  const location = useLocation()
  
  // Use provided items or generate from current path
  const breadcrumbItems = items || generateBreadcrumbsFromPath(location.pathname)

  // Don't render if only one item (current page)
  if (breadcrumbItems.length <= 1) {
    return null
  }

  return (
    <MuiBreadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      aria-label={t('common.breadcrumbs')}
      sx={{ mb: 2 }}
    >
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1

        if (isLast) {
          return (
            <Typography
              key={item.label}
              color="text.primary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
              }}
            >
              {item.icon}
              {item.label}
            </Typography>
          )
        }

        return (
          <Link
            key={item.label}
            component={RouterLink}
            to={item.path!}
            underline="hover"
            color="inherit"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {item.icon}
            {item.label}
          </Link>
        )
      })}
    </MuiBreadcrumbs>
  )
}
